/**
 * SALTMARCH — the Verdant Charter's harbour town on the drowned coast.
 *
 * Deliberately the opposite of Cinderhold in every readable way: flat and open
 * instead of a crater, cool green-grey instead of ember orange, timber and
 * thatch instead of dark stone, and built along a waterfront rather than around
 * a square. A player who arrives here should know instantly they have left.
 */
import { wood } from './scatter.js';
/* Half the terrain, so no wood is planted off the edge of the map. */
const HALF_MAP = [110, 110];
const SALTMARCH_BUILDINGS = [
    // --- the waterfront -----------------------------------------------------
    {
        id: 'harbourmaster', name: 'Harbourmaster', landmark: 'dock', lit: true,
        position: [0, -14], size: [16, 10], height: 8,
        walls: 'timber', roof: 'gable', roofSurface: 'slate', doorFacing: 'north'
    },
    {
        id: 'net-house', name: 'The Net House', landmark: 'tavern', lit: true,
        position: [-20, -8], size: [13, 10], height: 7,
        walls: 'darkTimber', roof: 'gable', roofSurface: 'thatch', doorFacing: 'east'
    },
    {
        id: 'charter-store', name: 'Charter Supply', landmark: 'store', lit: true,
        position: [21, -7], size: [12, 9], height: 6.5,
        walls: 'timber', roof: 'gable', roofSurface: 'thatch', doorFacing: 'west'
    },
    {
        id: 'boatwright', name: "Ilsa's Boatwright", landmark: 'blacksmith',
        position: [-34, -20], size: [15, 11], height: 7,
        walls: 'darkTimber', roof: 'gable', roofSurface: 'timber', rotation: 0.35, doorFacing: 'east'
    },
    // --- the inland rise ----------------------------------------------------
    {
        id: 'charter-hall', name: 'The Charter Hall', landmark: 'townhall', lit: true,
        position: [0, 30], size: [20, 14], height: 10.5,
        walls: 'plaster', roof: 'hip', roofSurface: 'slate', doorFacing: 'south'
    },
    {
        id: 'green', name: 'Training Green', landmark: 'training',
        position: [24, 24], size: [9, 7], height: 4.5,
        walls: 'timber', roof: 'flat', roofSurface: 'timber', doorFacing: 'west'
    },
    {
        id: 'greenhouse', name: 'Seed Houses', position: [-25, 26], size: [16, 9], height: 5.5,
        walls: 'plaster', roof: 'gable', roofSurface: 'tile', rotation: -0.2, lit: true
    },
    // --- stilt houses along the shore ---------------------------------------
    { id: 'stilt-1', name: 'Stilt House', position: [-13, -26], size: [7, 6], height: 5, rotation: 0.2, walls: 'darkTimber', roof: 'gable', roofSurface: 'thatch', lit: true },
    { id: 'stilt-2', name: 'Stilt House', position: [-4, -30], size: [7, 6], height: 5, rotation: -0.1, walls: 'timber', roof: 'gable', roofSurface: 'thatch' },
    { id: 'stilt-3', name: 'Stilt House', position: [6, -30], size: [7, 6], height: 5, rotation: 0.15, walls: 'darkTimber', roof: 'gable', roofSurface: 'thatch', lit: true },
    { id: 'stilt-4', name: 'Stilt House', position: [15, -26], size: [7, 6], height: 5, rotation: -0.25, walls: 'timber', roof: 'gable', roofSurface: 'thatch' },
    // --- cottages inland ----------------------------------------------------
    { id: 'cottage-1', name: 'Cottage', position: [-36, 12], size: [8, 7], height: 5, rotation: 0.4, walls: 'plaster', roof: 'gable', roofSurface: 'thatch', lit: true },
    { id: 'cottage-2', name: 'Cottage', position: [-40, 34], size: [8, 7], height: 5, rotation: -0.3, walls: 'timber', roof: 'gable', roofSurface: 'thatch' },
    { id: 'cottage-3', name: 'Cottage', position: [36, 12], size: [8, 7], height: 5, rotation: -0.4, walls: 'plaster', roof: 'gable', roofSurface: 'thatch', lit: true },
    { id: 'cottage-4', name: 'Cottage', position: [40, 36], size: [8, 7], height: 5, rotation: 0.25, walls: 'timber', roof: 'gable', roofSurface: 'thatch' },
    { id: 'cottage-5', name: 'Cottage', position: [-18, 46], size: [9, 7], height: 5.5, rotation: 0.1, walls: 'plaster', roof: 'gable', roofSurface: 'tile', lit: true },
    { id: 'cottage-6', name: 'Cottage', position: [18, 47], size: [9, 7], height: 5.5, rotation: -0.1, walls: 'timber', roof: 'gable', roofSurface: 'thatch' },
    { id: 'north-gate', name: 'Marsh Road Gate', position: [0, 62], size: [12, 7], height: 8, walls: 'timber', roof: 'flat', roofSurface: 'timber', doorFacing: 'south', lit: true }
];
export const SALTMARCH = {
    id: 'saltmarch',
    name: 'Saltmarch',
    kind: 'town',
    blurb: 'A Charter harbour built on stilts over the drowned coast. Nets, tides and quiet argument.',
    // On the main road below the Charter Hall, facing south down to the harbour.
    // The Hall occupies z = 23..37, so the old spawn at z = 34 was literally
    // inside the building.
    spawn: [0, 0, 12],
    spawnFacing: Math.PI,
    terrain: {
        size: [220, 220],
        surface: 'grass',
        terraces: [
            // A single low rise inland, so the town reads as sloping down to the water.
            { id: 'inland-rise', centre: [0, 46], innerRadius: 0, outerRadius: 62, height: 1.8, surface: 'grass' }
        ]
    },
    ambience: {
        skyTop: '#1f3242',
        skyBottom: '#9fb8b4',
        fogColour: '#8ea3a2',
        fogDensity: 0.0085,
        sunColour: '#e8f0e4',
        sunDirection: [0.38, -0.66, -0.64],
        ambientColour: '#3d5158'
    },
    buildings: SALTMARCH_BUILDINGS,
    props: [
        { id: 'sm-signpost', kind: 'signpost', position: [3, 6] },
        /* Off to one side of the spawn, not on it. It used to sit at [0, 12] — the
           exact spawn point — so the first thing anybody saw on arriving in the
           Reach was the inside of a well ring filling the screen. */
        { id: 'sm-well', kind: 'well', position: [-4.5, 13.5] },
        // the quay
        { id: 'boat-1', kind: 'boat', position: [-9, -42], rotation: 0.3 },
        { id: 'boat-2', kind: 'boat', position: [2, -45], rotation: -0.2, scale: 1.15 },
        { id: 'boat-3', kind: 'boat', position: [13, -42], rotation: 0.5, scale: 0.9 },
        { id: 'net-1', kind: 'net', position: [-16, -36], rotation: 0.4 },
        { id: 'net-2', kind: 'net', position: [10, -36], rotation: -0.3 },
        { id: 'net-3', kind: 'net', position: [-2, -38], rotation: 0.1 },
        { id: 'sm-stall-1', kind: 'stall', position: [-9, 2], rotation: 0.15 },
        { id: 'sm-stall-2', kind: 'stall', position: [9, 2], rotation: -0.15 },
        { id: 'sm-stall-3', kind: 'stall', position: [0, -2], rotation: 3.14 },
        { id: 'sm-lamp-1', kind: 'lamp', position: [-14, -18] },
        { id: 'sm-lamp-2', kind: 'lamp', position: [14, -18] },
        { id: 'sm-lamp-3', kind: 'lamp', position: [-14, 16] },
        { id: 'sm-lamp-4', kind: 'lamp', position: [14, 16] },
        { id: 'sm-lamp-5', kind: 'lamp', position: [0, 44] },
        { id: 'sm-barrel-1', kind: 'barrel', position: [-24, -13] }, { id: 'sm-barrel-2', kind: 'barrel', position: [-25.3, -12] },
        { id: 'sm-barrel-3', kind: 'barrel', position: [25, -12] },
        { id: 'sm-crate-1', kind: 'crate', position: [-6, -20] }, { id: 'sm-crate-2', kind: 'crate', position: [-7.4, -19], scale: 0.85 },
        { id: 'sm-crate-3', kind: 'crate', position: [7, -20] },
        { id: 'sm-cart-1', kind: 'cart', position: [20, 6], rotation: -0.8 },
        // marsh trees, thicker inland
        { id: 'sm-tree-1', kind: 'tree', position: [-48, 4], scale: 1.1 },
        { id: 'sm-tree-2', kind: 'tree', position: [-54, 22], scale: 1.25 },
        { id: 'sm-tree-3', kind: 'tree', position: [-46, 48], scale: 1 },
        { id: 'sm-tree-4', kind: 'tree', position: [50, 2], scale: 1.15 },
        { id: 'sm-tree-5', kind: 'tree', position: [56, 26], scale: 1.3 },
        { id: 'sm-tree-6', kind: 'tree', position: [48, 50], scale: 1.05 },
        { id: 'sm-tree-7', kind: 'tree', position: [-30, 60], scale: 1.2 },
        { id: 'sm-tree-8', kind: 'tree', position: [30, 60], scale: 1.1 },
        { id: 'sm-tree-9', kind: 'tree', position: [-8, 58], scale: 0.95 },
        { id: 'sm-tree-10', kind: 'tree', position: [12, 56], scale: 1.15 },
        { id: 'sm-rock-1', kind: 'rock', position: [-40, -44], scale: 1.6 },
        { id: 'sm-rock-2', kind: 'rock', position: [38, -46], scale: 1.9 },
        { id: 'sm-fence-1', kind: 'fence', position: [20, 30], rotation: 0 },
        { id: 'sm-fence-2', kind: 'fence', position: [28, 30], rotation: 0 },
        { id: 'sm-fence-3', kind: 'fence', position: [-22, 34], rotation: 1.5708 },
        /* Woods.
         *
         * The town used to stand in an empty field with ten trees around the edge,
         * which is what made the horizon read as the end of a level rather than as
         * more country. Three stands, none of them over the roads or the square:
         * scrub close in on both flanks, and a proper wood behind the north gate.
         * Trees do not collide — walking through a bush is a better outcome than a
         * player wedged behind one on a phone.
         */
        ...wood('sm-west', {
            centre: [-58, 26], innerRadius: 8, outerRadius: 32, count: 34, seed: 811,
            avoid: SALTMARCH_BUILDINGS, bounds: HALF_MAP,
            // Marsh scrub: low, wet, more bush than timber.
            mix: ['tree', 'tree', 'bush', 'bush', 'bush', 'deadtree', 'stump', 'reeds', 'reeds'],
            scale: 0.85
        }),
        ...wood('sm-east', {
            centre: [58, 32], innerRadius: 8, outerRadius: 28, count: 30, seed: 4127,
            avoid: SALTMARCH_BUILDINGS, bounds: HALF_MAP,
            mix: ['tree', 'tree', 'bush', 'bush', 'deadtree', 'stump', 'reeds'],
            scale: 0.9
        }),
        ...wood('sm-north', {
            centre: [0, 74], innerRadius: 14, outerRadius: 46, count: 46, seed: 90210,
            avoid: SALTMARCH_BUILDINGS, bounds: HALF_MAP,
            scale: 1.05
        }),
        // Reed beds along the waterline, where the ground meets the harbour.
        ...wood('sm-reeds', {
            centre: [0, -30], innerRadius: 24, outerRadius: 62, count: 26, seed: 5150,
            avoid: SALTMARCH_BUILDINGS, bounds: HALF_MAP,
            mix: ['reeds', 'reeds', 'reeds', 'bush', 'stump'],
            scale: 1
        })
    ],
    roads: [
        { id: 'sm-quay', points: [[-30, -34], [0, -34], [30, -34]], width: 8, surface: 'timber' },
        { id: 'sm-main', points: [[0, -30], [0, -10], [0, 10], [0, 26], [0, 44], [0, 60]], width: 7, surface: 'road' },
        { id: 'sm-cross', points: [[-34, 4], [-16, 4], [16, 4], [34, 4]], width: 6, surface: 'road' },
        { id: 'sm-hall-path', points: [[0, 20], [0, 24]], width: 5, surface: 'stone' }
    ],
    water: [
        // The drowned coast. Sits below ground level so the shoreline reads.
        { id: 'harbour', position: [0, -62], size: [200, 70], level: -1.6 }
    ],
    adSlots: [
        {
            id: 'SALTMARCH-QUAY-BILLBOARD-01', label: 'Quayside billboard',
            position: [-22, 4.4, -30], rotation: 0, size: [6.8, 3.8],
            format: 'billboard', showOnMobile: true, showOnDesktop: true, viewDistance: 55
        },
        {
            id: 'SALTMARCH-NETHOUSE-POSTER-01', label: 'Net House wall poster',
            position: [-13.6, 3.1, -8], rotation: -Math.PI / 2, size: [2.4, 3.4],
            format: 'poster', showOnMobile: false, showOnDesktop: true, viewDistance: 28
        },
        {
            id: 'SALTMARCH-SUPPLY-SIGN-01', label: 'Charter Supply shop sign',
            position: [15.2, 3.9, -7], rotation: -Math.PI / 2, size: [3.4, 1.3],
            format: 'shopSign', showOnMobile: true, showOnDesktop: true, viewDistance: 32
        },
        {
            id: 'SALTMARCH-STALL-SPONSOR-01', label: 'Sponsored market stall',
            position: [0, 2.7, -2], rotation: Math.PI, size: [3, 1.2],
            format: 'stall', showOnMobile: true, showOnDesktop: true, viewDistance: 26
        },
        {
            id: 'SALTMARCH-GATE-BANNER-01', label: 'Marsh road gate banner',
            position: [0, 5, 58.4], rotation: Math.PI, size: [5.6, 2.4],
            format: 'banner', showOnMobile: true, showOnDesktop: true, viewDistance: 46
        }
    ],
    bounds: { min: [-78, -50], max: [78, 78] },
    exits: [
        { id: 'to-cinderhold', toZone: 'cinderhold', position: [0, 66], radius: 5, label: 'Cinderhold' }
    ]
};
//# sourceMappingURL=saltmarch.js.map